import { useState } from 'react';
import { toast } from 'sonner';
import { TextField, BrandLoading } from '@scripsteam/scrips-signal-ds';
import { DatePickerField } from '@/shared/ui/components';
import { CountryDialPicker } from '@scripsteam/scrips-signal-ds';
import { Button, Select } from '@scripsteam/scrips-signal-ds';
import { FieldLabel } from '@scripsteam/scrips-signal-ds';
import { UploadImageDialog } from '@scripsteam/scrips-signal-ds';
import {
  useUploadImage,
  readFileAsBase64,
  IMAGE_AREAS,
} from '@/services/common';
import {
  useGenderMaster,
  usePractitionerById,
  useSaveProviderIntake,
} from '@/settings/services';
import type {
  Practitioner,
  PractitionerAddress,
  PractitionerName,
} from '@/types/settings-api';
import { SETTINGS_SECTION_TITLE } from '@/settings/components/settings-look';

/**
 * Practitioner Personal Details tab — PROD-761.
 *
 * Fully wired against `GET /api/v1/Practice/Practitioner/{userId}` and
 * `POST /api/Provider/Intake`. Round-trip symmetric — request body
 * is the exact same shape as the GET response.
 *
 * Flutter source: `doctor_personl_tab.dart` + `ProfileInfo` model.
 *
 * Fields (in Flutter order):
 *   - ACCOUNT PHOTO — `UploadImageDialog` + `useUploadImage(area="DOCTOR")`,
 *     the same Azure flow Staff (PROD-789) and Practice (PROD-790) use.
 *     `area: "DOCTOR"` is the verbatim Flutter constant (`app_constants.dart`,
 *     `imageAreaDoctor`). The returned URL is written to `draft.photo` and
 *     persisted by the existing POST /api/Provider/Intake save.
 *   - Title prefix + First Name + Middle Name + Last Name (PractitionerName)
 *   - Date of Birth
 *   - Gender (Master/Gender lookup)
 *   - Email
 *   - Phone (format: +CC-digits, matches Staff pattern)
 *   - Address (first entry — multi-address support deferred)
 */
export interface PractitionerPersonalTabProps {
  userId: string;
  canEdit: boolean;
  onSaveSuccess?: () => void;
}

const EMPTY_ADDRESS: PractitionerAddress = {
  id: null,
  country: '',
  state: '',
  city: '',
  addressLine1: '',
  addressLine2: null,
  postalCode: '',
  lat: null,
  lang: null,
};

export function PractitionerPersonalTab({
  userId,
  canEdit,
  onSaveSuccess,
}: PractitionerPersonalTabProps) {
  const { data: practitioner, isLoading } = usePractitionerById(userId);
  const { data: genders = [] } = useGenderMaster();
  const saveMutation = useSaveProviderIntake();
  const uploadImage = useUploadImage();

  const [draft, setDraft] = useState<Practitioner | null>(null);

  // Seed the draft from the loaded record by adjusting state *during render*
  // rather than in an effect — avoids the synchronous-setState-in-effect
  // cascading-render warning (same fix as staff-edit-page.tsx, PROD-967).
  // Re-seeds only when the route's userId changes; a same-id refetch keeps
  // in-flight edits.
  const [seededId, setSeededId] = useState<string | null>(null);
  if (practitioner && seededId !== userId) {
    setSeededId(userId);
    setDraft(practitioner);
  }

  if (isLoading || !draft) {
    return (
      <BrandLoading size={48} label="Loading practitioner details…" />
    );
  }

  const handlePhotoChange = async (file: File | null) => {
    if (file === null) {
      setDraft({ ...draft, photo: null });
      return;
    }
    try {
      const base64String = await readFileAsBase64(file);
      const url = await uploadImage.mutateAsync({
        base64String,
        area: IMAGE_AREAS.DOCTOR,
      });
      if (url) {
        setDraft({ ...draft, photo: url });
      } else {
        // Dedup re-upload returned an empty body — keep the existing URL.
        // See use-upload-image.ts header for the contract.
        toast.success('Photo uploaded.');
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to upload photo.');
    }
  };

  const setName = (patch: Partial<PractitionerName>) => {
    setDraft({ ...draft, name: { ...draft.name, ...patch } });
  };

  const setAddress = (patch: Partial<PractitionerAddress>) => {
    const current = draft.address[0] ?? EMPTY_ADDRESS;
    setDraft({ ...draft, address: [{ ...current, ...patch }, ...draft.address.slice(1)] });
  };

  const handleSave = () => {
    if (!draft.name.firstName.trim() || !draft.name.lastName.trim()) {
      toast.error('First name and last name are required.');
      return;
    }
    if (!draft.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(draft.email)) {
      toast.error('Please enter a valid email.');
      return;
    }
    saveMutation.mutate(draft, {
      onSuccess: () => {
        toast.success('Personal details saved');
        onSaveSuccess?.();
      },
      onError: (err) => {
        toast.error(err instanceof Error ? err.message : 'Save failed');
      },
    });
  };

  const address = draft.address[0] ?? EMPTY_ADDRESS;

  return (
    <div className="space-y-6">
      <h2 className={SETTINGS_SECTION_TITLE}>
        Personal details
      </h2>
      <PlainCard>
        <div data-slot="settings-photo">
          <UploadImageDialog
            label="Account photo"
            value={draft.photo}
            onChange={handlePhotoChange}
            readOnly={!canEdit}
          />
        </div>

        <div className="grid grid-cols-name-fields gap-3">
          <div>
            <FieldLabel stacked>Prefix</FieldLabel>
            <TextField
              value={draft.name.prefix ?? ''}
              placeholder="Dr."
              disabled={!canEdit}
              onChange={(e) => setName({ prefix: e.target.value || null })}
            />
          </div>
          <div>
            <FieldLabel stacked>
              First name <span className="text-status-error">*</span>
            </FieldLabel>
            <TextField
              value={draft.name.firstName}
              disabled={!canEdit}
              onChange={(e) => setName({ firstName: e.target.value })}
            />
          </div>
          <div>
            <FieldLabel stacked>Middle name</FieldLabel>
            <TextField
              value={draft.name.middleName}
              disabled={!canEdit}
              onChange={(e) => setName({ middleName: e.target.value })}
            />
          </div>
        </div>

        <div>
          <FieldLabel stacked>
            Last name <span className="text-status-error">*</span>
          </FieldLabel>
          <TextField
            value={draft.name.lastName}
            disabled={!canEdit}
            onChange={(e) => setName({ lastName: e.target.value })}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <FieldLabel stacked>Date of birth</FieldLabel>
            <DatePickerField
              value={draft.birthDate ? draft.birthDate.slice(0, 10) : ''}
              disabled={!canEdit}
              max={new Date()}
              onChange={(v) =>
                setDraft({
                  ...draft,
                  birthDate: v ? `${v}T00:00:00.000` : null,
                })
              }
            />
          </div>
          <div>
            <FieldLabel stacked>Gender</FieldLabel>
            <Select
              value={draft.gender ?? undefined}
              disabled={!canEdit}
              onChange={(next) => setDraft({ ...draft, gender: next || null })}
              placeholder="Select gender"
              options={genders.map((g) => ({ label: g.name, value: g.id }))}
            />
          </div>
        </div>
      </PlainCard>

      <h2 className={SETTINGS_SECTION_TITLE}>
        Contact details
      </h2>
      <PlainCard>
        <div>
          <FieldLabel stacked>
            Email <span className="text-status-error">*</span>
          </FieldLabel>
          <TextField
            type="email"
            value={draft.email}
            disabled={!canEdit}
            onChange={(e) => setDraft({ ...draft, email: e.target.value })}
          />
        </div>
        <div>
          <FieldLabel stacked>Phone number</FieldLabel>
          <CountryDialPicker
            value={draft.phonNo ?? ''}
            disabled={!canEdit}
            onChange={(v) => setDraft({ ...draft, phonNo: v })}
          />
        </div>
      </PlainCard>

      <h2 className={SETTINGS_SECTION_TITLE}>
        Address
      </h2>
      <PlainCard>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <FieldLabel stacked>Country</FieldLabel>
            <TextField
              value={address.country}
              disabled={!canEdit}
              onChange={(e) => setAddress({ country: e.target.value })}
            />
          </div>
          <div>
            <FieldLabel stacked>State</FieldLabel>
            <TextField
              value={address.state}
              disabled={!canEdit}
              onChange={(e) => setAddress({ state: e.target.value })}
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <FieldLabel stacked>City</FieldLabel>
            <TextField
              value={address.city}
              disabled={!canEdit}
              onChange={(e) => setAddress({ city: e.target.value })}
            />
          </div>
          <div>
            <FieldLabel stacked>Postal code</FieldLabel>
            <TextField
              value={address.postalCode}
              disabled={!canEdit}
              onChange={(e) => setAddress({ postalCode: e.target.value })}
            />
          </div>
        </div>
        <div>
          <FieldLabel stacked>Address line</FieldLabel>
          <TextField
            value={address.addressLine1}
            disabled={!canEdit}
            onChange={(e) => setAddress({ addressLine1: e.target.value })}
          />
        </div>
      </PlainCard>

      {canEdit && (
        <div className="flex justify-end">
          <Button
            type="button"
            onClick={handleSave}
            disabled={saveMutation.isPending}
          >
            {saveMutation.isPending ? 'Saving…' : 'Save Personal Details'}
          </Button>
        </div>
      )}
    </div>
  );
}

function PlainCard({ children }: { children: React.ReactNode }) {
  return (
    <section className="rounded-lg border border-border-hairline bg-surface-card shadow-1 px-6 py-6 flex flex-col gap-6">
      {children}
    </section>
  );
}
